<template>
  <v-expansion-panel :value="panelValue" v-bind:readonly="readonly" @group:selected="onExpansion">
    <v-expansion-panel-title>
      <v-icon v-if="route.cancelled" color="error" class="mr-2">mdi-cancel</v-icon>
      <v-row no-gutters>
        <v-col v-if="showDate" :class="dateClass">
          {{ toDate(route.date) }}<span class="same-run" v-if="!showTrainNumber && route.sameRunAs && route.sameRunAs.length" title="Fuhr auch unter dieser Zugnummer"> (<v-icon size="small">mdi-swap-horizontal-variant</v-icon> <template v-for="(twin, index) in route.sameRunAs" v-bind:key="twin.trainNumber"><span v-if="index > 0"> / </span><TrainLink :category="twin.category" :trainNumber="twin.trainNumber"/></template>)</span>
        </v-col>
        <v-col v-if="showDate" :class="shortDateClass">
          {{ toShortDate(route.date) }}
        </v-col>
        <v-col v-if="realTrip && showTrainNumber">
          <TrainLink class="enum" :category="realTrip.category" :trainNumber="realTrip.trainNumber"/><span v-if="route.relatedTrip"> (<v-icon size="small">mdi-cached</v-icon> {{ route.relatedTrip.category }} {{ route.relatedTrip.trainNumber }})</span><span class="same-run" v-if="route.sameRunAs && route.sameRunAs.length" title="Fuhr auch unter dieser Zugnummer"> (<v-icon size="small">mdi-swap-horizontal-variant</v-icon> <template v-for="(twin, index) in route.sameRunAs" v-bind:key="twin.trainNumber"><span v-if="index > 0"> / </span><TrainLink :category="twin.category" :trainNumber="twin.trainNumber"/></template>)</span>
          <span title="Abweichende Wagenreihung" v-if="route.reverseOrder"><v-icon size="small">mdi-swap-horizontal-variant</v-icon></span>
        </v-col>
        <v-col v-if="route.vehicles && showVehicles">
          <VehicleLink class="enum" v-for="vehicle in route.vehicles" v-bind:key="vehicle" :vehicle="vehicle"/>
          <span title="Abweichende Wagenreihung" v-if="route.reverseOrder && !showTrainNumber"><v-icon size="small">mdi-swap-horizontal-variant</v-icon></span>
        </v-col>
        <v-col valign="middle">
          <v-icon
            v-if="hasNotification"
            class="notify-flag"
            icon="mdi-bell-ring"
            size="x-small"
            color="primary"
            title="Benachrichtigung aktiv"
            aria-label="Benachrichtigung aktiv"
          />
          <div class="delay-row" v-if="displayDelayStatistics && !route.cancelled">
            <div class="delay-bar-wrap">
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <v-progress-linear v-bind="props" :model-value="delayRating" v-bind:color="delayColor" height="6"/>
                </template>
                <span>Gewichtetes Mittel: {{ formatDelay(displayDelayStatistics.weightedAverageDelay, false) }},  Max: {{ formatDelay(displayDelayStatistics.maxDelay, false) }}, Min: {{ formatDelay(displayDelayStatistics.minDelay, false) }}, Median: {{ formatDelay(displayDelayStatistics.medianDelay, false) }}</span>
              </v-tooltip>
            </div>
            <span class="delay-min font-mono" :class="`text-${delayTextColor}`" :title="delayTitle">{{ delayMinutes }}</span>
          </div>
        </v-col>
      </v-row>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <div v-if="loading" class="lazy-state">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>
      <div v-else-if="loadFailed" class="lazy-state text-medium-emphasis text-body-2">
        Details konnten nicht geladen werden.
      </div>
      <template v-else>
        <div v-if="showLiveStatus || canNotify || hasNotification" class="trip-toolbar">
          <div
            v-if="showLiveStatus"
            class="live-status text-caption"
            :class="isStale ? 'text-warning' : 'text-medium-emphasis'"
          >
            <span v-if="isLive && !isStale" class="live-dot" aria-hidden="true"></span>
            <span>{{ liveStatusText }}</span>
            <v-btn
              class="live-refresh-btn"
              icon="mdi-refresh"
              size="x-small"
              variant="text"
              :loading="refreshing"
              title="Jetzt aktualisieren"
              aria-label="Jetzt aktualisieren"
              @click="refreshNow"
            />
          </div>
          <v-btn
            v-if="canNotify || hasNotification"
            class="notify-btn"
            :prepend-icon="hasNotification ? 'mdi-bell-ring' : 'mdi-bell-outline'"
            :color="hasNotification ? 'primary' : undefined"
            size="small"
            variant="text"
            @click="notifyDialog = true"
          >{{ hasNotification ? 'Benachrichtigung aktiv' : 'Benachrichtigen' }}</v-btn>
        </div>
        <NotificationDialog
          v-model="notifyDialog"
          :trip-id="route.id"
          :train-label="notificationTrainLabel"
          :stops="displayStops"
        />
        <div v-if="delayCurveStops.length > 1" style="height: 220px" class="mb-3">
          <LineChart :datasets="delayCurve.datasets" :labels="delayCurve.labels" :options="curveOptions"/>
        </div>
        <Stops :stops="displayStops" :hasVehicles="route.vehicles" />
      </template>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script>
import { DateTime } from 'luxon'
import DateTimeMixin from '@/mixins/DateTimeMixin'
import DelayMixin from '@/mixins/DelayMixin'
import Stops from './Stops.vue'
import LineChart from '@/components/LineChart.vue'
import TrainLink from '@/components/TrainLink.vue'
import VehicleLink from '@/components/VehicleLink.vue'
import VehicleMixin from '@/mixins/VehicleMixin'
import LiveRefreshMixin from '@/mixins/LiveRefreshMixin'
import NotificationMixin from '@/mixins/NotificationMixin'
import NotificationDialog from '@/components/NotificationDialog.vue'

// How long after its last stop a trip still counts as live. IRIS reports the
// final stops with a lag, so stopping exactly at arrival would drop the last
// update.
const LIVE_GRACE_MINUTES = 15

// Delay data does not change faster than this, and a slower cadence keeps a
// forgotten tab from hammering bahn-stats.
const REFRESH_INTERVAL_MS = 60000

// Consecutive failures before the status row admits the numbers are aging.
const STALE_AFTER_FAILURES = 3

// Consecutive failures before the poller gives up altogether. isLive only ever
// changes when a refresh lands, so a run of failures can never end the polling
// on its own — without a ceiling an expired OIDC session (no silent renew, no
// offline_access) turns into one 401 per minute for as long as the panel stays
// open. The row stays visible in its stale form and the manual button can
// still revive the poller.
const STOP_AFTER_FAILURES = 10

export default {
  name: 'Trip',
  mixins: [DateTimeMixin, DelayMixin, LiveRefreshMixin, NotificationMixin, VehicleMixin],
  props: {
    route: Object,
    trip: Object,
    showDate: {
      type: Boolean,
      default: true,
    },
    showTrainNumber: {
      type: Boolean,
      default: true,
    },
    showVehicles: {
      type: Boolean,
      default: true,
    },
    // When set, the trip is rendered from the Summary view (no stops) and its
    // stops are fetched lazily the first time the panel is expanded. Used by
    // the leaderboard so the home page does not have to fetch the Details view
    // for every trip up front.
    lazy: {
      type: Boolean,
      default: false,
    },
    // Stable identifier for this panel within its <v-expansion-panels> group,
    // used to persist/restore which day is open across refreshes. When unset
    // Vuetify falls back to the panel's index (the previous behavior).
    panelValue: {
      type: [String, Number],
      default: undefined,
    },
  },

  components: {
    Stops,
    LineChart,
    TrainLink,
    VehicleLink,
    NotificationDialog,
  },

  data: () => ({
    loadedStops: null,
    loadedDelayStatistics: null,
    loading: false,
    loadFailed: false,
    lastRefreshedAt: null,
    refreshFailures: 0,
    refreshing: false,
    wasLive: false,
    notifyDialog: false,
    // Whether this panel is currently open. Tracked so a deferred
    // onPanelOpened() callback (see loadStops()'s pending case) can tell it
    // has gone stale — the panel collapsed, or the component unmounted —
    // before acting on it.
    panelOpen: false,
    curveOptions: {
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { title: { display: true, text: 'Verspätung/min' } } },
    },
  }),

  computed: {
    delayColor: function() {
      return this.delayToTriStateColor(this.displayDelayStatistics.weightedAverageDelay);
    },

    // The upcoming stop while the train is en route: the first not-yet-reached
    // stop, unless that is the origin (then it hasn't departed yet). null when
    // the trip is finished or has no stop data.
    nextStop: function() {
      var stops = this.displayStops;
      if (!stops || !stops.length) return null;
      var now = DateTime.local();
      for (var i = 0; i < stops.length; i++) {
        var stop = stops[i];
        var time = this.effectiveTime(stop.departure || stop.arrival);
        if (!time) continue;
        if (now < DateTime.fromISO(time, { zone: 'UTC' })) {
          return i === 0 ? null : stop;
        }
      }
      return null;
    },

    // Effective time of the last stop that has one, as an ISO string. null when
    // there are no stops yet (a lazy trip that has never been expanded).
    lastStopTime: function() {
      var stops = this.displayStops;
      if (!stops || !stops.length) return null;
      for (var i = stops.length - 1; i >= 0; i--) {
        var time = this.effectiveTime(stops[i].arrival || stops[i].departure);
        if (time) return time;
      }
      return null;
    },

    // Whether the journey is still ahead of us or under way — the condition for
    // auto-refreshing. effectiveTime() follows the real (delayed) time, so a
    // train running 40 minutes late stays live 40 minutes longer with no extra
    // rule, and a trip that has not departed yet is live because its predicted
    // delays still move.
    isLive: function() {
      // A cancelled trip is never going to move again, so there is nothing to
      // poll for and nothing that may call itself "Live" under a cancel icon.
      if (this.route.cancelled) return false;

      var end = this.lastStopTime;
      if (end == null) return false;

      var endsAt = DateTime.fromISO(end, { zone: 'UTC' }).plus({ minutes: LIVE_GRACE_MINUTES });
      return DateTime.local() < endsAt;
    },

    refreshIntervalMs: function() {
      return REFRESH_INTERVAL_MS;
    },

    // Notifications only make sense for a journey that is still ahead or under
    // way. Unlike isLive there is no grace period: once the last stop is past,
    // there is nothing left to announce.
    canNotify: function() {
      if (this.route.cancelled || !this.route.id) return false;
      var end = this.lastStopTime;
      return end != null && DateTime.local() < DateTime.fromISO(end, { zone: 'UTC' });
    },

    hasNotification: function() {
      return this.notificationFor(this.route.id) != null;
    },

    notificationTrainLabel: function() {
      var trip = this.realTrip;
      return trip ? `${trip.category} ${trip.trainNumber}` : '';
    },

    // The status row is shown for any panel that has been polling in this
    // session, and stays after the journey ends so the last timestamp remains
    // visible instead of vanishing mid-view.
    showLiveStatus: function() {
      return this.wasLive && !this.loading && !this.loadFailed;
    },

    isStale: function() {
      return this.refreshFailures >= STALE_AFTER_FAILURES;
    },

    // The poller has hit its failure ceiling and must not be armed again until
    // a refresh succeeds (which resets refreshFailures).
    gaveUp: function() {
      return this.refreshFailures >= STOP_AFTER_FAILURES;
    },

    lastRefreshedLabel: function() {
      return this.lastRefreshedAt
        ? this.lastRefreshedAt.setZone('Europe/Berlin').toFormat('HH:mm')
        : null;
    },

    liveStatusText: function() {
      if (this.isLive && !this.isStale) {
        return this.lastRefreshedLabel
          ? `Live · aktualisiert ${this.lastRefreshedLabel}`
          : 'Live';
      }

      return this.lastRefreshedLabel
        ? `Zuletzt aktualisiert ${this.lastRefreshedLabel}`
        : 'Zuletzt aktualisiert –';
    },

    // The live delay while the train runs. While it is standing at a stop
    // (already arrived but not yet departed) this is that stop's departure
    // delay; while en route to the next stop it is that stop's (predicted)
    // arrival delay. null once the trip is finished or no live data exists.
    liveDelay: function() {
      var stop = this.nextStop;
      if (!stop) return null;
      var arrivalTime = this.effectiveTime(stop.arrival);
      var atStop = arrivalTime != null
        && DateTime.local() >= DateTime.fromISO(arrivalTime, { zone: 'UTC' });
      var ad = atStop
        ? (stop.departure || stop.arrival)
        : (stop.arrival || stop.departure);
      if (!ad || ad.cancelled || ad.delayMinutes == null) return null;
      return { station: stop.station, delayMinutes: ad.delayMinutes, atStop: atStop };
    },

    nextStopDelay: function() {
      return this.liveDelay != null ? this.liveDelay.delayMinutes : null;
    },

    // While the train runs, show the live delay; otherwise the weighted
    // average over the whole trip.
    displayDelay: function() {
      return this.nextStopDelay != null
        ? this.nextStopDelay
        : this.displayDelayStatistics.weightedAverageDelay;
    },

    delayTextColor: function() {
      // Map the tri-state color to readable theme text colors.
      var color = this.delayToTriStateColor(this.displayDelay);
      return color === 'green' ? 'success' : color === 'yellow' ? 'warning' : 'error';
    },

    delayMinutes: function() {
      // Signed delay rounded to whole minutes (e.g. +24 / -2).
      return this.formatDelay(Math.round(this.displayDelay), false);
    },

    delayTitle: function() {
      var live = this.liveDelay;
      if (live == null) return 'Gewichtete mittlere Verspätung';
      return live.atStop
        ? `Verspätung bei Abfahrt (${live.station})`
        : `Verspätung am nächsten Halt (${live.station})`;
    },

    delayCurveStops: function() {
      return (this.displayStops || []).filter(stop => !stop.cancelled);
    },

    delayCurve: function() {
      var stops = this.delayCurveStops;
      return {
        labels: stops.map(stop => stop.station),
        datasets: [{
          label: 'Ø Verspätung (min)',
          data: stops.map(stop => stop.averageDelayMinutes),
          borderColor: '#1976d2',
          backgroundColor: '#1976d2',
          pointRadius: 2,
          fill: false,
          tension: 0.2,
        }],
      };
    },

    delayHistory: function() {
      return this.route.stops.filter(stop => !stop.cancelled).map(stop => stop.averageDelayMinutes);
    },

    delayHistoryColors: function() {
      return this.route.stops.filter(stop => !stop.cancelled).map(stop => this.delayToColor(stop.averageDelay));
    },

    delayHistoryStations: function() {
      return this.route.stops.filter(stop => !stop.cancelled).map(stop => stop.station);
    },

    delayRating: function() {
      return Math.min(this.displayDelayStatistics.weightedAverageDelay, 120) / 120 * 100;
    },

    readonly: function() {
      // Lazy trips have no stops yet but must stay expandable so the first
      // expand can trigger the fetch.
      return !this.lazy && this.displayStops == null;
    },

    // The stops to render: lazily-loaded ones take precedence, otherwise
    // whatever the route already carries (Details view).
    displayStops: function() {
      return this.loadedStops || this.route.stops;
    },

    // Same precedence for the trip-wide statistics behind the progress bar,
    // its tooltip and displayDelay's post-trip fallback. Without this they
    // would stay frozen at their page-load values while the panel refreshes,
    // so the last number a user sees after watching a live journey would be
    // the one they started with.
    displayDelayStatistics: function() {
      return this.loadedDelayStatistics || this.route.delayStatistics;
    },

    realTrip: function() {
      if (this.trip != null) {
        return this.trip
      } else if (this.route != null && this.route.trip != null) {
        return this.route.trip
      }

      return null
    },

    dateClass: function() {
      if (this.showTrainNumber && this.showVehicles) {
        return "d-none d-sm-flex"
      } else {
        return ""
      }
    },

    shortDateClass: function() {
      if (this.showTrainNumber && this.showVehicles) {
        return "d-flex d-sm-none"
      } else {
        return "d-none"
      }
    },

    vehicles: function() {
      var vehicles = this.route.vehicles;
      if (vehicles.length > 0) {
        return vehicles.map(v => this.prettyVehicleName(v)).join(", ");
      } else {
        return null;
      }
    },
  },

  methods: {
    // Effective time of an arrival/departure as an ISO string: the real
    // (current) time, falling back to the planned time when missing or
    // cancelled. null when there is no arrival/departure.
    effectiveTime: function(ad) {
      if (!ad) return null;
      return ad.cancelled ? ad.plannedTime : (ad.currentTime || ad.plannedTime);
    },

    onExpansion: function(event) {
      if (event && event.value) {
        this.panelOpen = true;
        // Each open is a fresh session: forgive whatever failures piled up
        // the last time this panel was open, so a give-up does not become
        // permanent just because the panel got collapsed and reopened.
        this.refreshFailures = 0;
        this.onPanelOpened();
      } else {
        this.panelOpen = false;
        this.stopLiveRefresh();
        this.wasLive = false;
      }
    },

    onPanelOpened: function() {
      // A lazy trip has no stops to judge liveness by until the first load
      // lands, so wait for it when there is one to wait for.
      var pending = this.loadStops();

      if (pending) {
        // The panel may have been collapsed, or this component unmounted,
        // before the load lands — panelOpen tracks that so the callback
        // knows it has gone stale and does not arm a refresh nobody sees.
        pending.then(() => {
          if (this.panelOpen) this.startLiveRefreshIfLive();
        });
      } else {
        this.startLiveRefreshIfLive();
      }
    },

    startLiveRefreshIfLive: function() {
      if (!this.isLive || this.gaveUp) return;

      this.wasLive = true;
      this.startLiveRefresh();
    },

    // The mixin's tick: refetch, then decide whether there is any point in
    // another tick.
    onLiveRefresh: function() {
      return this.refreshStops().then(() => {
        if (!this.isLive || this.gaveUp) this.stopLiveRefresh();
      });
    },

    // Immediate refresh behind the manual button. Stops the poller first so the
    // already-scheduled tick is dropped instead of firing a second request
    // seconds later, then re-arms it — which also revives a poller that had
    // stopped because the journey looked over or the failures had piled up.
    // The request can outlive the panel (collapsed, or the component
    // unmounted) while it is in flight, so — exactly like onPanelOpened()'s
    // deferred branch — the continuation only re-arms when panelOpen is still
    // true. stopLiveRefresh() above already leaves the poller stopped, so
    // skipping the re-arm here is enough; there is nothing left to undo.
    refreshNow: function() {
      if (this.refreshing) return Promise.resolve();

      this.stopLiveRefresh();

      return this.refreshStops().then(() => {
        if (this.panelOpen) this.startLiveRefreshIfLive();
      });
    },

    // The bare request for this trip's Details view — one trip per request, the
    // backend overflows (HTTP 414) when several ids are batched. Resolves with
    // the trip, or null when the backend knows no such trip or it carries no
    // stops; rejects on transport errors.
    fetchTripDetails: function() {
      return this.$api
        .get(`/trip/${encodeURIComponent(this.route.id)}`)
        .then(response => {
          var trip = response.data && response.data[0]
          return trip == null || trip.stops == null ? null : trip
        })
    },

    // Adopt a freshly fetched Details view. delayStatistics is optional in a
    // way stops are not, so a response without it keeps the previous numbers
    // rather than blanking the headline.
    applyTripDetails: function(trip) {
      this.loadedStops = trip.stops
      if (trip.delayStatistics != null) {
        this.loadedDelayStatistics = trip.delayStatistics
      }
      this.lastRefreshedAt = DateTime.local()
    },

    // Fetch this trip's stops the first time the panel is opened. No-op for
    // non-lazy trips or once the stops have been loaded; returns null then, so
    // callers can tell "nothing to wait for" from a pending request.
    loadStops: function() {
      if (!this.lazy || this.loading || this.loadedStops != null || this.route.stops != null) {
        return null;
      }

      this.loading = true
      this.loadFailed = false

      return this.fetchTripDetails()
        .then(trip => {
          if (trip == null) {
            this.loadFailed = true
            return
          }
          this.applyTripDetails(trip)
        })
        .catch(err => {
          this.$log.warn(`Could not load trip details for ${this.route.id}: ${err}`)
          this.loadFailed = true
        })
        .finally(() => {
          this.loading = false
        })
    },

    // A background refresh. Unlike loadStops() it deliberately refetches details
    // the view already has — that is the whole point — and never touches
    // loading/loadFailed: no spinner may flash over content being read.
    // Never rejects.
    refreshStops: function() {
      this.refreshing = true

      return this.fetchTripDetails()
        .then(trip => {
          if (trip == null) {
            this.$log.warn(`Refresh returned no trip for ${this.route.id}`)
            this.refreshFailures = this.refreshFailures + 1
            return
          }
          this.applyTripDetails(trip)
          this.refreshFailures = 0
        })
        .catch(err => {
          this.$log.warn(`Could not refresh trip details for ${this.route.id}: ${err}`)
          this.refreshFailures = this.refreshFailures + 1
        })
        .finally(() => {
          this.refreshing = false
        })
    },
  },

  beforeUnmount: function() {
    // LiveRefreshMixin's own beforeUnmount already stops any active
    // refresh; this just invalidates a deferred onPanelOpened() callback
    // that may still be waiting on a lazy load in flight.
    this.panelOpen = false;
  },
};
</script>

<style scoped>
/* Secondary info riding along in the date column ("auch ICE 2793"): keep it visually quiet so
   the column still reads as a date column. */
.same-run {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.delay-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.delay-bar-wrap {
  flex: 1 1 auto;
  min-width: 0;
}
.delay-min {
  flex: none;
  min-width: 3.5ch;
  text-align: right;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
}
.lazy-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 18px 0;
}
.trip-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 8px;
}
.notify-btn {
  margin-left: auto;
}
.notify-flag {
  float: right;
  margin-left: 6px;
}
.live-status {
  display: flex;
  align-items: center;
  gap: 8px;
}
.live-dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgb(var(--v-theme-success));
  animation: live-pulse 2s ease-in-out infinite;
}
.live-refresh-btn {
  margin-left: auto;
}
@keyframes live-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
  .live-dot { animation: none; }
}

/* On phones Vuetify's 24px side padding costs the stops table a seventh of the
   viewport it needs to fit all three columns. */
@media (max-width: 599.98px) {
  :deep(.v-expansion-panel-text__wrapper) {
    padding: 8px 12px 16px;
  }
}
</style>
